import { useState } from "react";
import { useTranslation } from "react-i18next";
import { installConnectorUpdate } from "../../../api/connectorsApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import { appPaths } from "../../../shared/routing/appPaths";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import ButtonLink from "../../../ui/buttons/ButtonLink";
import { useFollowPc } from "../pcPage/followPc";
import type { UpdateOffer } from "./connectorUpdate";

interface PcUpdateProps {
  readonly connectorId: string;
  readonly offer: UpdateOffer;
}

/**
 * Update for one PC (F159): one click, which that PC's tray turns into check, download,
 * signature check and install. The page then follows its PCs (F161), and the line says it is
 * updating, until the connector comes back with the new version and the line goes. When the
 * follow ends first, the button returns: the tray installs only once the PC's turns have ended.
 * A PC without a tray, or with a connector from before it could be asked, gets the release's
 * installer instead.
 */
export default function PcUpdate({ connectorId, offer }: PcUpdateProps) {
  const { t } = useTranslation();
  const { state, submit } = useSubmission();
  const { following, follow } = useFollowPc();
  const [asked, setAsked] = useState(false);
  const [needsInstaller, setNeedsInstaller] = useState(false);
  // The follow ended without the new version: Update is offered again (render-time adjustment).
  if (asked && !following) setAsked(false);

  if (offer.through === "installer" || needsInstaller) {
    return (
      <ButtonLink to={appPaths.connectorSetup()} variant="primary" size="xs">
        {t("connectors.list.getVersion", { version: offer.version })}
      </ButtonLink>
    );
  }
  if (asked) return <span className="app-text-muted">{t("connectors.list.updating")}</span>;
  return (
    <>
      <AppButton
        type="button"
        variant="primary"
        size="xs"
        disabled={state.status === "submitting"}
        onClick={() => {
          submit(async () => {
            const outcome = await installConnectorUpdate(connectorId);
            if (outcome === "sent") {
              follow();
              setAsked(true);
            } else if (outcome === "update_needed") setNeedsInstaller(true);
          });
        }}
      >
        {t("connectors.list.update", { version: offer.version })}
      </AppButton>
      {state.status === "failed" ? <ErrorNotice error={state.error} /> : null}
    </>
  );
}
